import { computed } from "vue";
import { fabric } from "fabric";
import WebFont from "webfontloader";

import { store } from "@/store";

import { ActionTypes } from "@/store/actions";

const currentLabel = computed(() => store.getters.currentLabel);
const templateJson = computed(() => store.getters.templateAndLabel.templateJson);
const userArtworkObjects = computed(() => store.getters.userArtworkFetch.fabric_objects);
const isRotated = computed<boolean>(() =>
  store.getters.canvasOrientation === "rotated" ? true : false
);

const loadArtworkFonts = (objects: any): Promise<void> => {
  return new Promise((resolve) => {
    const fontsToLoad: string[] = [];

    objects.forEach((obj: any) => {
      if (obj.type === "i-text") {
        let font = obj.fontFamily;
        let firstAdded = false;

        if (obj.fontStyle !== "normal" && obj.fontWeight !== "normal") {
          font += ":700i";
          firstAdded = true;
        }

        if (obj.fontWeight !== "normal") {
          if (firstAdded) {
            font += ",700";
          } else {
            font += ":700";
          }
        }

        if (obj.fontStyle !== "normal") {
          if (firstAdded) {
            font += ",400i";
          } else {
            font += ":400i";
          }
        }

        fontsToLoad.push(font);
      }
    });

    if (fontsToLoad.length) {
      WebFont.load({
        google: {
          families: fontsToLoad,
        },
        active: function() {
          resolve();
        },
      });
    } else {
      resolve();
    }
  });
};

export const loadJson = async (canvas: fabric.Canvas, objectId: number): Promise<number> => {
  const fabricJSON = userArtworkObjects.value ? userArtworkObjects.value : templateJson.value;

  await loadArtworkFonts(fabricJSON.objects);

  const canvasCenter = canvas.getCenter();
  const JSONCanvasCenter = fabricJSON.canvasCenter;

  let adjustLeft = canvasCenter.left;
  let adjustTop = canvasCenter.top;

  if (JSONCanvasCenter) {
    adjustLeft -= JSONCanvasCenter.left;
    adjustTop -= JSONCanvasCenter.top;
  }

  return new Promise((resolve) => {
    store.dispatch(ActionTypes.SET_LABEL_BG_COLOR, fabricJSON.backgroundColor);

    fabric.util.enlivenObjects(
      fabricJSON.objects,
      function(objects: any) {
        objects.forEach((obj: any) => {
          obj.set({
            id: objectId,
            left: obj.left + adjustLeft,
            top: obj.top + adjustTop,
          });

          objectId += 1;

          canvas.add(obj);
        });

        canvas.renderAll();
        resolve(objectId);
      },
      ""
    );
  });
};

export const loadOldEditorJson = async (
  canvas: fabric.Canvas,
  objectId: number
): Promise<number> => {
  const fabricJSON = userArtworkObjects.value ? userArtworkObjects.value : templateJson.value;

  await loadArtworkFonts(fabricJSON.objects);

  return new Promise((resolve) => {
    switch (currentLabel.value.shape_id) {
      case 3:
      case 4: {
        store.dispatch(ActionTypes.SET_LABEL_BG_COLOR, fabricJSON.background);

        const bleedLine: any = canvas._objects.find((obj: any) => {
          return obj.name === "bleedLine";
        });

        fabric.util.enlivenObjects(
          fabricJSON.objects,
          function(objects: any) {
            objects.forEach((obj: any) => {
              obj.set({
                name: obj.type,
                id: objectId,
                left: isRotated.value
                  ? bleedLine.aCoords.bl.x + obj.left
                  : bleedLine.aCoords.tl.x + obj.left,
                top: isRotated.value
                  ? bleedLine.aCoords.bl.y + obj.top
                  : bleedLine.aCoords.tl.y + obj.top,
              });

              switch (obj.name) {
                case "i-text": {
                  obj.set("name", "iText");
                  break;
                }
                case "path": {
                  obj.set("name", "svgShape");
                  break;
                }
              }

              objectId += 1;

              const preCenter = obj.calcCoords();

              obj.set({
                originX: "center",
                originY: "center",
              });

              const afterCenter = obj.calcCoords();

              obj.set({
                left: obj.left + (preCenter.tl.x - afterCenter.tl.x),
                top: obj.top + (preCenter.tl.y - afterCenter.tl.y),
              });

              canvas.add(obj);
            });

            canvas.renderAll();
            resolve(objectId);
          },
          ""
        );
        break;
      }
      case 2:
      case 5:
      case 6:
      case 7:
      case 8:
      case 10: {
        store.dispatch(ActionTypes.SET_LABEL_BG_COLOR, fabricJSON.background);

        const bleedLine: any = canvas._objects.find((obj: any) => {
          return obj.name === "bleedLine";
        });

        fabric.util.enlivenObjects(
          fabricJSON.objects,
          function(objects: any) {
            objects.forEach((obj: any, idx: number) => {
              obj.set({
                name: obj.type,
                id: objectId,
                left: isRotated.value
                  ? bleedLine.aCoords.bl.x + obj.left
                  : bleedLine.aCoords.tl.x + obj.left,
                top: isRotated.value
                  ? bleedLine.aCoords.bl.y + obj.top
                  : bleedLine.aCoords.tl.y + obj.top,
              });

              switch (obj.name) {
                case "i-text": {
                  obj.set("name", "iText");
                  break;
                }
                case "path": {
                  obj.set("name", "svgShape");
                  break;
                }
              }

              objectId += 1;

              const preCenter = obj.calcCoords();

              obj.set({
                originX: "center",
                originY: "center",
              });

              const afterCenter = obj.calcCoords();

              obj.set({
                left: obj.left + (preCenter.tl.x - afterCenter.tl.x),
                top: obj.top + (preCenter.tl.y - afterCenter.tl.y),
              });

              canvas.add(obj);
            });

            canvas.renderAll();
            resolve(objectId);
          },
          ""
        );
        break;
      }
      default: {
        resolve(objectId);
        break;
      }
    }
  });
};
